/**
 * Auto-detect property type based on location title keywords.
 *
 * v2 taxonomy contract (live since 2026-05-24):
 *   - 60 flat L1 place types (no parent/child hierarchy): the 38 the client sheet
 *     defines plus the 22 it dropped, which keep their listings.
 *   - Resolution is by slug (e.g. `apartment-condo`, `restaurant-cafe`).
 *   - There is no L2 "category" any more — `typeGroupId` is always null.
 *
 * Detection model:
 *   - Each rule maps a regex to a v2 L1 slug with a confidence weight.
 *   - On match, the rule's confidence is added to the slug's running score.
 *   - The slug with the highest score wins, ties broken by the first rule
 *     to score (i.e. by rule order). Returning null is preferred over
 *     guessing — partner sync is production-critical and a wrong type is
 *     worse than no type.
 */

interface DetectionResult {
  locationTypeId: string;
}

interface TaxonomyLocationType {
  id: string;
  name: string;
  slug?: string | null;
  parentId?: string | null;
}

interface V2KeywordRule {
  /** v2 L1 slug — the stable identifier for the place type */
  slug: string;
  /** Regex matched against the location name (case-insensitive expected) */
  pattern: RegExp;
  /** Confidence weight added to the slug's score when this rule matches */
  confidence: number;
}

/**
 * Keyword rules covering the L1 place types.
 *
 * The seven slugs the 31 July 2026 release renamed carry their NEW spelling here: the
 * detection result is resolved against the live `type` table, and the old spellings stop
 * being live slugs at the cutover.
 *
 * Confidence guidance:
 *   - 0.9-0.95: highly specific anchors (e.g. "photography studio", "mansion")
 *   - 0.8-0.85: strong but somewhat ambiguous anchors (e.g. "hotel", "apartment")
 *   - 0.6-0.75: contextual cues with broader meaning (e.g. "bar", "studio")
 *
 * Order does not affect correctness (scoring is commutative), but multiple
 * rules can contribute to the same slug — a listing matching both "loft" and
 * "industrial" hints would still resolve to the strongest match thanks to
 * scoring.
 */
/**
 * The seven categories the 31 July 2026 release renames: retired spelling -> canonical one.
 * Mirrors `docs/contracts/type-slug-aliases.json`.
 */
const CANONICAL_SLUG_BY_RETIRED: Record<string, string> = {
  house: 'houses',
  office: 'offices',
  'bank-financial-branch': 'banks',
  'industrial-building-factory': 'industrial-factory',
  'farm-ranch': 'farm-rural',
  'sound-stage': 'set-build-soundstage',
  'retail-small-business': 'shops-storefronts',
};

/** The same map inverted, so a canonical slug can find a live row that still carries the old one. */
const RETIRED_SLUG_BY_CANONICAL: Record<string, string> = Object.fromEntries(
  Object.entries(CANONICAL_SLUG_BY_RETIRED).map(([retired, canonical]) => [canonical, retired])
);

const V2_KEYWORD_RULES: V2KeywordRule[] = [
  // --- Residential ---
  { slug: 'apartment-condo', pattern: /\b(apartment|condo|condominium|penthouse|apt|flat)\b/i, confidence: 0.9 },
  { slug: 'houses', pattern: /\b(house|home|villa|bungalow|cottage|townhouse)\b/i, confidence: 0.85 },
  { slug: 'loft', pattern: /\bloft\b/i, confidence: 0.85 },
  { slug: 'mansion-estate', pattern: /\b(mansion|estate|manor|chateau)\b/i, confidence: 0.9 },

  // Bare "studio" in vacation-rental titles overwhelmingly means studio
  // apartment (not photography studio). Lower confidence so a specific
  // "photography studio" / "film studio" rule wins.
  { slug: 'apartment-condo', pattern: /\bstudio\b/i, confidence: 0.6 },

  // --- Hospitality & Nightlife ---
  { slug: 'hotel-motel', pattern: /\b(hotel|motel|hostel|inn|resort)\b/i, confidence: 0.85 },
  { slug: 'restaurant-cafe', pattern: /\b(restaurant|cafe|café|coffee\s*shop|bistro|diner|eatery|brasserie)\b/i, confidence: 0.9 },
  { slug: 'bar-club', pattern: /\b(bar|pub|club|nightclub|lounge|tavern|cocktail)\b/i, confidence: 0.75 },
  { slug: 'brewery-distillery', pattern: /\b(brewery|distillery|winery\s*tasting)\b/i, confidence: 0.9 },

  // --- Retail & Services ---
  { slug: 'shops-storefronts', pattern: /\b(shop|store|retail|boutique|deli|grocery|market)\b/i, confidence: 0.8 },
  { slug: 'salon-spa-barber-shop', pattern: /\b(salon|spa|barber|hairdresser)\b/i, confidence: 0.9 },
  { slug: 'gallery-showroom', pattern: /\b(gallery|showroom)\b/i, confidence: 0.85 },

  // --- Offices & Institutions ---
  { slug: 'offices', pattern: /\b(office|co-?working|workspace)\b/i, confidence: 0.85 },
  { slug: 'banks', pattern: /\b(bank|financial\s*branch)\b/i, confidence: 0.9 },
  { slug: 'school-university-campus', pattern: /\b(school|university|campus|college|academy|institute)\b/i, confidence: 0.85 },
  { slug: 'medical-clinic-hospital', pattern: /\b(clinic|hospital|medical|doctor|infirmary)\b/i, confidence: 0.9 },

  // --- Culture & Events ---
  { slug: 'event-space', pattern: /\b(event\s*space|venue|ballroom|banquet\s*hall)\b/i, confidence: 0.85 },
  { slug: 'theater-cinema', pattern: /\b(theater|theatre|cinema|movie\s*theater|playhouse)\b/i, confidence: 0.9 },
  { slug: 'sports-venue-stadium', pattern: /\b(stadium|arena|sports?\s*venue|sports?\s*ground)\b/i, confidence: 0.9 },
  { slug: 'museum', pattern: /\bmuseum\b/i, confidence: 0.95 },
  { slug: 'church-temple', pattern: /\b(church|temple|mosque|chapel|cathedral|synagogue|monastery)\b/i, confidence: 0.95 },
  { slug: 'cemetery-memorial', pattern: /\b(cemetery|graveyard|memorial)\b/i, confidence: 0.9 },
  { slug: 'gym-fitness-dance-studio', pattern: /\b(gym|fitness|dance\s*studio|yoga|pilates|crossfit)\b/i, confidence: 0.85 },

  // --- Industrial & Logistics ---
  { slug: 'industrial-factory', pattern: /\b(industrial|factory|plant|refinery|mill)\b/i, confidence: 0.85 },
  { slug: 'warehouse-storage', pattern: /\b(warehouse|storage|depot)\b/i, confidence: 0.9 },
  { slug: 'airport-hangar', pattern: /\b(airport|hangar|airfield|terminal)\b/i, confidence: 0.9 },

  // --- Rural / Natural ---
  { slug: 'farm-rural', pattern: /\b(farm|farmhouse|ranch|barn|stable|orchard)\b/i, confidence: 0.9 },
  { slug: 'forest-heath', pattern: /\b(forest|woodland|heath|grove)\b/i, confidence: 0.85 },
  { slug: 'mountains-cliffs', pattern: /\b(mountain|mountains|cliff|cliffs|peak|summit|alpine)\b/i, confidence: 0.85 },
  { slug: 'park-garden-parkland', pattern: /\b(park|garden|parkland|botanical)\b/i, confidence: 0.75 },
  { slug: 'riverside', pattern: /\b(riverside|riverfront|river\s*bank|lakefront|lakeside)\b/i, confidence: 0.85 },

  // --- Urban / Streetscapes ---
  { slug: 'alley-grit', pattern: /\b(alley|alleyway|back\s*street|grit)\b/i, confidence: 0.85 },
  { slug: 'bridge', pattern: /\b(bridge|viaduct|overpass)\b/i, confidence: 0.85 },
  { slug: 'residential-street', pattern: /\b(residential\s*street|suburban\s*street|neighborhood\s*street)\b/i, confidence: 0.9 },
  { slug: 'town', pattern: /\b(town|village|townscape|town\s*square)\b/i, confidence: 0.7 },
  { slug: 'wasteland', pattern: /\b(wasteland|derelict|abandoned\s*lot|ruins)\b/i, confidence: 0.85 },

  // --- Studio types (production) ---
  // Order matters here only for human readability — scoring still picks the
  // most specific match because specific rules have higher confidence.
  { slug: 'set-build-soundstage', pattern: /\b(sound\s*stage|soundstage|film\s*stage|movie\s*set)\b/i, confidence: 0.95 },
  { slug: 'tv-studio-stage', pattern: /\b(tv\s*studio|television\s*studio|broadcast\s*studio)\b/i, confidence: 0.95 },
  { slug: 'photography-studio', pattern: /\b(photo\s*studio|photography\s*studio|photoshoot\s*studio)\b/i, confidence: 0.95 },
  { slug: 'recording-studio', pattern: /\b(recording\s*studio|music\s*studio|sound\s*booth)\b/i, confidence: 0.95 },
  // Note: there is no v2 "film studio" L1 — film studios resolve via "sound stage".

  // --- Vehicles: road ---
  { slug: 'car', pattern: /\b(car|automobile|sedan|coupe|sports?\s*car|classic\s*car)\b/i, confidence: 0.85 },
  { slug: 'motorcycle', pattern: /\b(motorcycle|motorbike|scooter)\b/i, confidence: 0.9 },
  { slug: 'truck', pattern: /\b(truck|lorry|semi-?truck|pickup)\b/i, confidence: 0.85 },
  { slug: 'van-bus-rv', pattern: /\b(van|bus|coach|rv|motorhome|campervan|minibus)\b/i, confidence: 0.85 },

  // --- Vehicles: water / air / rail ---
  { slug: 'boat-yacht', pattern: /\b(boat|yacht|ship|sailboat|catamaran|cruiser|vessel)\b/i, confidence: 0.9 },
  { slug: 'dock-marina', pattern: /\b(dock|marina|harbour|harbor|pier|wharf|quay)\b/i, confidence: 0.9 },
  { slug: 'airplane', pattern: /\b(airplane|aeroplane|plane|aircraft|jet)\b/i, confidence: 0.9 },
  { slug: 'helicopter', pattern: /\b(helicopter|chopper|heli)\b/i, confidence: 0.9 },
  { slug: 'train', pattern: /\b(train|locomotive|railway\s*carriage|train\s*station)\b/i, confidence: 0.9 },
  // --- 31 July 2026 categories -------------------------------------------------------
  // Deliberately narrow anchors: a broad word already scores for a category that is being
  // retired, and those still resolve — a write on a retired category follows its successor
  // pointer. What these add is the ability to detect the new categories at all, which
  // nothing could before they existed.
  { slug: 'urban-city-exterior', pattern: /\b(city\s*exterior|downtown|old\s*town|public\s*square|street\s*corner)\b/i, confidence: 0.75 },
  { slug: 'nature-landscape', pattern: /\b(landscape|nature|scenic|wilderness)\b/i, confidence: 0.8 },
  { slug: 'road-highway-parking-gas', pattern: /\b(highway|motorway|gas\s*station|petrol\s*station|parking\s*(?:lot|garage)|roadside)\b/i, confidence: 0.85 },
  { slug: 'police-court-prison', pattern: /\b(police\s*station|court\s*house|courthouse|prison|jail|precinct)\b/i, confidence: 0.9 },
  { slug: 'gyms-pools-leisure', pattern: /\b(casino|bowling|amusement\s*park|golf\s*course|ski\s*resort|swimming\s*pool|playground)\b/i, confidence: 0.85 },
  { slug: 'entertainment-performance', pattern: /\b(concert\s*hall|opera\s*house|music\s*venue|performance\s*venue)\b/i, confidence: 0.85 },
  { slug: 'military-security-bunker', pattern: /\b(military|barracks|bunker|armou?ry|guard\s*post)\b/i, confidence: 0.9 },
  { slug: 'government-civic', pattern: /\b(city\s*hall|town\s*hall|embassy|library|archive|post\s*office|community\s*cent(?:er|re))\b/i, confidence: 0.85 },
  { slug: 'lab-research', pattern: /\b(laborator(?:y|ies)|research\s*(?:facility|cent(?:er|re))|science\s*lab)\b/i, confidence: 0.9 },
  { slug: 'landmarks-castles', pattern: /\b(castle|ancient\s*ruins|monument|observation\s*deck|landmark)\b/i, confidence: 0.9 },
  { slug: 'public-transport-station', pattern: /\b((?:train|metro|bus|subway)\s*station|bus\s*stop|tram\s*stop)\b/i, confidence: 0.9 },
  { slug: 'bus-coach', pattern: /\b(coach|school\s*bus|city\s*bus|minibus)\b/i, confidence: 0.85 },
];

/**
 * Score a text against all v2 keyword rules and return the slug with the
 * highest cumulative confidence. Returns null when nothing matches.
 */
function bestSlugForText(text: string): { slug: string; score: number } | null {
  if (!text) {
    return null;
  }

  const scores = new Map<string, number>();
  for (const rule of V2_KEYWORD_RULES) {
    if (rule.pattern.test(text)) {
      const prev = scores.get(rule.slug) ?? 0;
      scores.set(rule.slug, prev + rule.confidence);
    }
  }

  if (scores.size === 0) {
    return null;
  }

  // Pick highest score; on tie, the first inserted slug wins (Map preserves
  // insertion order, which mirrors rule order — a useful, deterministic
  // tiebreaker).
  let bestSlug: string | null = null;
  let bestScore = -1;
  for (const [slug, score] of scores) {
    if (score > bestScore) {
      bestSlug = slug;
      bestScore = score;
    }
  }

  return bestSlug ? { slug: bestSlug, score: bestScore } : null;
}

/**
 * Resolve a slug to a taxonomy entry, returning a DetectionResult.
 * Returns null if the slug is not present in the taxonomy (e.g. taxonomy
 * fetch failed or upstream renamed the slug).
 */
function resolveSlug(
  slug: string,
  taxonomy: { locationTypes: TaxonomyLocationType[] }
): DetectionResult | null {
  // RESOLVE-FIRST, THEN THE RETIRED SPELLING. The 31 July 2026 release renames seven
  // categories, and the rules above carry their NEW spelling — but bridge deploys on its own
  // pipeline and reads the live `type` list, which keeps the OLD spelling until the cutover
  // migration runs (days later: the release stages its rows inactive first). Without the
  // fallback, detection for house/office/bank/shop/industrial/farm/soundstage would silently
  // return null for that whole window and every such sync would be flagged for manual review.
  //
  // Mirrors docs/contracts/type-slug-aliases.json, the same map backend's TypeSlugAliases and
  // frontend-client's TypeSlugRedirects carry. Both directions are tried, so this is also
  // correct if a rule is ever written with the retired spelling.
  const match =
    taxonomy.locationTypes.find((t) => t.slug === slug) ??
    taxonomy.locationTypes.find((t) => t.slug === RETIRED_SLUG_BY_CANONICAL[slug]) ??
    taxonomy.locationTypes.find((t) => t.slug === CANONICAL_SLUG_BY_RETIRED[slug]);

  if (!match) {
    return null;
  }
  return {
    locationTypeId: match.id,
  };
}

/**
 * Detect property type from location name and/or partner property type.
 * Tries matching the location name first, then falls back to the partner's
 * property type. Returns null if no match found.
 *
 * @param locationName - The location's display name/title
 * @param taxonomy - The ScoutAround taxonomy with v2 L1 location types
 * @param partnerPropertyType - Optional property type from the partner (e.g., "Apartment")
 */
export function detectPropertyType(
  locationName: string,
  taxonomy: { locationTypes: TaxonomyLocationType[] },
  partnerPropertyType?: string | null
): DetectionResult | null {
  const nameMatch = bestSlugForText(locationName);
  if (nameMatch) {
    const resolved = resolveSlug(nameMatch.slug, taxonomy);
    if (resolved) {
      return resolved;
    }
  }

  if (partnerPropertyType) {
    const partnerMatch = bestSlugForText(partnerPropertyType);
    if (partnerMatch) {
      return resolveSlug(partnerMatch.slug, taxonomy);
    }
  }

  return null;
}

/**
 * Exported for tests — the full ruleset, so we can assert the L1 slugs
 * have at least one keyword rule.
 */
export const __v2RulesForTests: readonly V2KeywordRule[] = V2_KEYWORD_RULES;
